import type { CSSProperties } from "react";
import { userAction } from "@/core/telemetry/userAction";
import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import { useClients } from "@/features/clients/hooks/clientQueries";
import { useServices } from "@/features/services/hooks/serviceQueries";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { StatusBadge } from "@/shared/components/ui/StatusBadge";
import { cn } from "@/shared/utils/cn";
import { formatBusinessDate, formatBusinessTime, getBusinessDateKey } from "@/shared/utils/formatters";
import type { CalendarEvent } from "../types/calendarEventTypes";
import { createDayTimeline, minuteOfDay, TIMELINE_PX_PER_MINUTE, timelineOffset } from "../utils/dayTimeline";
import { CalendarEventCard } from "./CalendarEventCard";

const closedBackground = "repeating-linear-gradient(135deg, hsl(var(--muted)) 0 5px, hsl(var(--background)) 5px 10px)";

interface CalendarDayTimelineProps {
  appointments: Appointment[];
  events: CalendarEvent[];
  dateKey: string;
  timezone: string;
  now: Date;
  openWindows: { startAt: Date; endAt: Date }[];
  gaps: { startAt: Date; endAt: Date }[];
  onAppointmentClick: (appointment: Appointment) => void;
  onEventClick: (event: CalendarEvent) => void;
  onGapAdd?: (startAt: Date) => void;
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

export function CalendarDayTimeline({
  appointments, events, dateKey, timezone, now, openWindows, gaps, onAppointmentClick, onEventClick, onGapAdd,
  isLoading, error, onRetry,
}: CalendarDayTimelineProps) {
  const services = useServices();
  const clients = useClients();
  if (error) return <ErrorState message={`This day could not be loaded: ${error.message}`} onRetry={onRetry} />;
  if (isLoading) return <div aria-label="Loading day"><RowSkeleton /></div>;

  const timeline = createDayTimeline({ appointments, events, dateKey, timezone, openWindows, gaps: onGapAdd ? gaps : [] });
  const time = (date: Date) => formatBusinessTime(date, timezone);
  const minuteLabel = (minute: number) => formatBusinessTime(new Date(Date.UTC(2000, 0, 1, 0, minute)), "UTC");
  const customerName = (appointment: Appointment) => {
    const client = clients.data?.find((item) => item.id === appointment.clientId);
    return client ? [client.firstName, client.lastName].filter(Boolean).join(" ") || "Customer" : "Customer";
  };

  if (!timeline.range) {
    return (
      <div className="space-y-2">
        {timeline.allDay.map((event) => <CalendarEventCard key={event.id} event={event} dateKey={dateKey} timezone={timezone} onClick={onEventClick} />)}
        <p className="rounded-xl px-3 py-2.5 text-xs text-muted-foreground" style={{ background: closedBackground }}>Closed</p>
      </div>
    );
  }

  const { startMinute, endMinute } = timeline.range;
  const position = (from: number, to: number): CSSProperties => ({
    top: timelineOffset(from, startMinute),
    height: (to - from) * TIMELINE_PX_PER_MINUTE,
  });
  const nowMinute = getBusinessDateKey(now, timezone) === dateKey ? minuteOfDay(now, dateKey, timezone) : null;

  return (
    <div className="space-y-2">
      {timeline.allDay.map((event) => <CalendarEventCard key={event.id} event={event} dateKey={dateKey} timezone={timezone} onClick={onEventClick} />)}
      <div className="relative ml-[52px] mr-1" style={{ height: (endMinute - startMinute) * TIMELINE_PX_PER_MINUTE }}>
        {timeline.closed.map((band) => (
          <div key={`closed-${band.startMinute}`} className="absolute inset-x-0" style={{ ...position(band.startMinute, band.endMinute), background: closedBackground }}>
            {(band.endMinute - band.startMinute) * TIMELINE_PX_PER_MINUTE >= 20 && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10.5px] text-muted-foreground">Closed</span>}
          </div>
        ))}
        {timeline.open.map((band) => (
          <div key={`open-${band.startMinute}`} className="absolute inset-x-0 border-l-[3px] border-border bg-card" style={position(band.startMinute, band.endMinute)} />
        ))}
        {timeline.hours.map((minute) => (
          <div key={minute} aria-hidden="true" className="pointer-events-none absolute inset-x-0 border-t border-border" style={{ top: timelineOffset(minute, startMinute) }}>
            <span className="absolute right-[calc(100%+8px)] top-0 -translate-y-1/2 whitespace-nowrap text-[10.5px] text-muted-foreground">{minuteLabel(minute)}</span>
          </div>
        ))}
        {onGapAdd && timeline.spots.map((spot, index) => (
          <button
            key={spot.startAt.getTime()}
            type="button"
            aria-label={`Add appointment at ${time(spot.startAt)}`}
            {...userAction("appointment.add_in_gap")}
            onClick={() => onGapAdd(spot.startAt)}
            className={cn(
              "group absolute left-[3px] right-0 flex items-center justify-between px-2.5 text-left text-[11px] text-muted-foreground/60 transition-colors",
              "hover:bg-secondary hover:font-bold hover:text-primary focus-visible:bg-secondary focus-visible:font-bold focus-visible:text-primary focus-visible:outline-none active:bg-secondary",
              timeline.spots[index - 1]?.endMinute === spot.startMinute && "border-t border-dashed border-border/70",
            )}
            style={position(spot.startMinute, spot.endMinute)}
          >
            <span>{time(spot.startAt)}</span>
            <span aria-hidden="true" className="text-base leading-none opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100">+</span>
          </button>
        ))}
        {timeline.blocks.map((block) => {
          const style: CSSProperties = {
            ...position(block.startMinute, block.endMinute),
            left: `calc(8px + (100% - 12px) * ${block.lane / block.lanes})`,
            width: `calc((100% - 12px) / ${block.lanes})`,
          };
          const range = `${time(block.startAt)}–${time(block.endAt)}`;
          if (block.kind === "event") {
            const title = block.event.title || (block.event.kind === "blocked" ? "Blocked time" : "Personal event");
            return (
              <button key={block.id} type="button" onClick={() => onEventClick(block.event)} aria-label={`${title}, ${range}`} className="absolute px-px pb-[3px] text-left focus-visible:outline-none [&:focus-visible>span]:ring-2 [&:focus-visible>span]:ring-ring" style={style}>
                <span className="block h-full overflow-hidden rounded-xl bg-lilac px-2.5 py-1.5 text-xs leading-tight text-lilac-foreground">
                  <strong className="block truncate text-[12.5px]">{title}</strong>
                  <span className="opacity-80">{range}</span>
                </span>
              </button>
            );
          }
          const { appointment } = block;
          const name = customerName(appointment);
          const serviceNames = [...appointment.services]
            .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))
            .map((line) => services.data?.find((service) => service.id === line.serviceId)?.name || "Unknown Service");
          const date = formatBusinessDate(block.startAt, timezone, { day: "numeric", month: "short" });
          const pending = appointment.status === "pending";
          return (
            <button key={block.id} type="button" onClick={() => onAppointmentClick(appointment)} aria-label={`${name}, ${date}, ${range}, ${serviceNames.join(", ") || "No Service"}${pending ? ", pending" : ""}`} data-telemetry-label="Appointment details" className="absolute px-px pb-[3px] text-left focus-visible:outline-none [&:focus-visible>span]:ring-2 [&:focus-visible>span]:ring-ring" style={style}>
              <span
                className={cn(
                  "block h-full overflow-y-auto rounded-xl px-2.5 py-1.5 text-xs leading-tight",
                  pending ? "bg-warning-foreground/60 text-foreground" : "bg-primary text-primary-foreground",
                )}
              >
                <strong className="flex items-center gap-1.5 text-[12.5px]">
                  <span data-telemetry-mask className="truncate">{name}</span>
                  {pending && <StatusBadge tone="pending" className="px-1.5 py-0 text-[10px]">Pending</StatusBadge>}
                </strong>
                <span className="block truncate opacity-80">{date} · {range}</span>
                {serviceNames.length > 0 ? (
                  <span role="list" aria-label="Services" className="block opacity-80">
                    {serviceNames.map((service, index) => <span key={index} role="listitem" className="block truncate">{service}</span>)}
                  </span>
                ) : <span className="block opacity-80">No Service</span>}
              </span>
            </button>
          );
        })}
        {nowMinute !== null && nowMinute >= startMinute && nowMinute <= endMinute && (
          <div aria-hidden="true" data-agenda-now className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-danger" style={{ top: timelineOffset(nowMinute, startMinute) }}>
            <span className="absolute -left-1 -top-[5px] size-2 rounded-full bg-danger" />
            <span className="absolute right-[calc(100%+5px)] top-[-1px] -translate-y-1/2 rounded-md bg-danger px-1 py-px text-[10.5px] font-bold text-danger-foreground">{time(now)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
